import React, { useState, useEffect } from 'react';
import { FileText, ChevronDown, RefreshCw } from 'lucide-react';
import { ResumeProtocol } from '../../types/resume';
import { Button } from '../common/Button';

interface ResumeSelectorProps {
  resumeList: ResumeProtocol[];
  selectedResume: ResumeProtocol | null;
  onChange: (resume: ResumeProtocol | null) => void;
  onRefresh: () => void;
  shouldShowRefresh?: boolean;
}

const ResumeSelector: React.FC<ResumeSelectorProps> = ({ 
  resumeList, 
  selectedResume, 
  onChange,
  onRefresh,
  shouldShowRefresh = true
}) => {
  const [showRefresh, setShowRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Reset states when resumeList changes
    setIsLoading(true);
    setShowRefresh(false);

    // Set up timeout to show refresh button after 3 seconds
    const timeoutId = setTimeout(() => {
      if (resumeList.length === 0) {
        setShowRefresh(true);
        setIsLoading(false);
      }
    }, 3000);

    // Clear timeout if resumes are loaded before 3 seconds
    if (resumeList.length > 0) {
      clearTimeout(timeoutId);
      setIsLoading(false);
    }

    return () => clearTimeout(timeoutId);
  }, [resumeList]);

  const formatDate = (date: string) => {
    // Format to 23 Apr 2025 12:00 PM
    return new Date(date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
          <div className={`flex items-center gap-2 ${!shouldShowRefresh ? 'mb-1' : ''}`}>
            <FileText size={18} className="text-primary-light dark:text-primary-dark" />
            <label className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark">
            Select Resume
          </label>
          </div>
        {shouldShowRefresh && (
          <Button
            variant="outline"
            size="sm"
          onClick={onRefresh}
          className="h-8 w-8 hover:bg-background-light/50 dark:hover:bg-background-dark/50 rounded-full transition-colors"
          title="Refresh resumes"
        >
            <RefreshCw size={16} className="text-text-tertiary-light dark:text-text-tertiary-dark" />
          </Button>
        )}
      </div>
      <div className="relative bg-background-light/70 dark:bg-background-dark/70 rounded-lg border border-border-light dark:border-border-dark px-1 py-1 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all">
        <select 
          value={selectedResume?._id || ''}
          onChange={(e) => {
            const resume = resumeList.find(r => r._id === e.target.value);
            onChange(resume || null);
          }}
          className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm appearance-none pr-8"
        >
          {resumeList.length > 0 ? resumeList.sort((a, b) => new Date(b.updatedAt || '').getTime() - new Date(a.updatedAt || '' ).getTime()).map(resume => (
            <option key={resume._id} value={resume._id}>
              {resume.personalDetails.name.split(' ')[0]} - {resume._id?.slice(-12)} - {formatDate(resume.updatedAt || '')}
            </option>
          )) : (
            <option value="">
              {isLoading ? "Loading resumes..." : "No Resume Found, Create one for best experience"}
            </option>
          )}
        </select>
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <ChevronDown size={16} className="text-text-tertiary-light dark:text-text-tertiary-dark" />
        </div>
      </div>
    </div>
  );
};

export default ResumeSelector; 